import React, { useEffect, useState, useCallback } from "react";
import { useParams, withRouter, Link } from "react-router-dom";
import { withModels } from "../../../models/queries";
import compose from "../../../utils/compose";
import pick from "../../../utils/pick";
import get from "../../../utils/get";
import drop from "../../../utils/drop";
import isEmpty from "../../../utils/isEmpty";
// styles
import styles from "./RecordDetail.module.css";

const pathToCollection = {
  limited_warranty: "/limited_warranty_form",
  cert: "/certificate_of_destruction_form",
  wastage: "/wastage_replacement_form",
};

const pathToDisplay = {
  cert: {
    header: "Certificate of Destruction",
  },
  limited_warranty: {
    header: "Limited Warranty Replacement Claim",
  },
  wastage: {
    header: "Wastage Replacement Claim",
  },
  licenseURL: {
    header: "License",
    format: (url) => (
      <a href={url} target="_blank">
        {" "}
        {"license"}{" "}
      </a>
    ),
  },
  destructionFormId: {
    header: "Destruction Form",
    format: (id) => <Link to={`/admin/cert/${id}`}> {id} </Link>,
  },
  authorizationVerification: {
    header: "Authorization Verification",
  },
  date: {
    header: "Date",
  },
  eSignature: {
    header: "E-Signature",
  },
  invoiceNumber: {
    header: "Invoice Number",
  },
  purchaseDate: {
    header: "Purchase Date",
  },
  requestedVials: {
    header: "Vials Requested",
  },
  termsVerify: {
    header: "Terms Accepted",
  },
  wastageApproved: {
    header: "Form Approved",
  },
  warrantyApproved: {
    header: "Form Approved",
  },
  destructionApproved: {
    header: "Form Approved",
  },
  administrationDate: {
    header: "Administration Date",
  },
  finalApproved: {
    header: "Final Approval",
  },
  address1: {
    header: "Address Line 1",
  },
  address2: {
    header: "Address Line 2",
  },
  city: {
    header: "City",
  },
  otherInformation: {
    header: "Other Information",
  },
  state: {
    header: "State",
  },
  zipCode: {
    header: "Zip Code",
  },
  authorizedRepresentativeName: {
    header: "Representative Name",
  },
  authorizedRepresentativeTitle: {
    header: "Representative Title",
  },
  email: {
    header: "Email",
  },
  hospitalName: {
    header: "Hospital Name",
  },
  hospitalAddress: {
    header: "Hospital Address",
  },
  phoneNumber: {
    header: "Phone Number",
  },
  purchaseOrderNumber: {
    header: "Purchase Order Number",
  },
};

function RecordDetail({ history, models }) {
  const { formId, form } = useParams();
  const [record, setRecord] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => {
    models.fetchRecord(pathToCollection[form], formId).then((record) => {
      setRecord(drop(record, "note"));
      setNote(get(record, "note"));
    });
  }, [formId, form, setRecord]);

  const submit = useCallback(
    function submitNote(e) {
      e.preventDefault();

      switch (form) {
        case "cert":
          models.updateDestructionRecord(formId, { note });
          break;
        case "limited_warranty":
          models.updateWarrantyRecord(formId, { note });
          break;
        case "wastage":
          models.updateWastageRecord(formId, { note });
          break;
      }
    },
    [form, formId, models, note]
  );

  if (!record) return "Loading...";

  const flat = drop(record, "shipping", "contact");
  const nested = pick(record, "shipping", "contact");
  return (
    <div className={styles.formContainer}>
      <h3 className={styles.parcelHeader}>
        {" "}
        {get(pathToDisplay, `${form}.header`)}{" "}
      </h3>
      <div className={styles.record}>
        <div className={`${styles.cell} ${styles.topLine}`}>
          <span className={styles.fieldName}> {"Record ID: "} </span>
          <span className={styles.fieldValue}> {flat._id} </span>
        </div>
        <div className={styles.moduleContainer}>
          <div className={styles.recordData}>
            {isEmpty(drop(flat, "_id", "createdAt")) ? (
              <span className={styles.fieldName}> {"Pending"} </span>
            ) : (
              Object.entries(drop(flat, "_id", "createdAt")).map(
                ([key, value]) => {
                  return (
                    <div className={styles.cell} key={`${form}-key-${key}`}>
                      <span className={styles.fieldName}>
                        {" "}
                        {(get(pathToDisplay, `${key}.header`) || key) +
                          ": "}{" "}
                      </span>
                      <span className={styles.fieldValue}>
                        {" "}
                        {(get(pathToDisplay, `${key}.format`) &&
                          get(pathToDisplay, `${key}.format`).call(
                            null,
                            value
                          )) ||
                          value.toString()}{" "}
                      </span>
                    </div>
                  );
                }
              )
            )}
          </div>
          <div className={styles.noteContainer}>
            <span className={styles.fieldName}> {"Note: "} </span>
            <textarea
              className={styles.note}
              placeholder="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <button
              className={styles.saveButton}
              onClick={submit}
              disabled={!note}
            >
              {" "}
              {"Save"}{" "}
            </button>
          </div>
        </div>
        <div className={styles.moduleContainer}>
          {nested.shipping && (
            <div className={styles.recordShipping}>
              <h5 className={styles.parcelHeader}>
                {" "}
                {"Shipping Information:"}{" "}
              </h5>
              {Object.entries(nested.shipping).map(([key, value]) => {
                return (
                  <div className={styles.cell} key={`${form}-key-${key}`}>
                    <span className={styles.fieldName}>
                      {" "}
                      {(get(pathToDisplay, `${key}.header`) || key) + ": "}{" "}
                    </span>
                    <span className={styles.fieldValue}>
                      {" "}
                      {value.toString()}{" "}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {nested.contact && (
            <div className={styles.recordContact}>
              <h5 className={styles.parcelHeader}>
                {" "}
                {"Contact Information:"}{" "}
              </h5>
              {Object.entries(nested.contact).map(([key, value]) => {
                return (
                  <div className={styles.cell} key={`${form}-key-${key}`}>
                    <span className={styles.fieldName}>
                      {" "}
                      {(get(pathToDisplay, `${key}.header`) || key) + ": "}{" "}
                    </span>
                    <span className={styles.fieldValue}>
                      {" "}
                      {value.toString()}{" "}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default compose(withRouter, withModels)(RecordDetail);
